import React, { useState, useEffect, useRef } from 'react';
import { Dropdown, Badge, Spinner, ListGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import './NotificationDropdown.css';

function NotificationDropdown({ token, unreadCount, onNotificationUpdate }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

  useEffect(() => {
    if (show && token) {
      fetchRecentNotifications();
    }
  }, [show, token]);

  const fetchRecentNotifications = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/users/social/notifications?limit=5`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId, e) => {
    e.stopPropagation();
    try {
      const response = await fetch(`${API_BASE_URL}/users/social/notifications/${notificationId}/read`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // Update local state
        setNotifications(prev =>
          prev.map(n => n.id === notificationId ? { ...n, is_read: 1 } : n)
        );
        // Notify parent to update count
        if (onNotificationUpdate) {
          onNotificationUpdate();
        }
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleDeleteNotification = async (notificationId, e) => {
    e.stopPropagation();
    try {
      const response = await fetch(`${API_BASE_URL}/users/social/notifications/${notificationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // Remove from local state
        setNotifications(prev => prev.filter(n => n.id !== notificationId));
        // Notify parent to update count
        if (onNotificationUpdate) {
          onNotificationUpdate();
        }
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read when clicked
    if (notification.is_read === 0) {
      handleMarkAsRead(notification.id, { stopPropagation: () => {} });
    }

    // Navigate based on notification type
    if (notification.related_user_id) {
      setShow(false);
      navigate(`/profile/${notification.related_user_id}`);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'verification':
        return '✓';
      case 'follow_request':
        return '👤';
      case 'follow_accepted':
      case 'invite_accepted': // #358
        return '✓';
      case 'new_follower':
        return '👥';
      case 'new_review':
        return '⭐';
      case 'restaurant_approved':
        return '🍽️';
      default:
        return '🔔';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'verification':
        return 'success';
      case 'follow_request':
        return 'primary';
      case 'follow_accepted':
      case 'invite_accepted': // #358
        return 'success';
      case 'new_follower':
        return 'info';
      case 'new_review':
        return 'warning';
      case 'restaurant_approved':
        return 'success';
      default:
        return 'secondary';
    }
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Dropdown show={show} onToggle={setShow} ref={dropdownRef} align="end">
      <Dropdown.Toggle
        variant="link"
        id="notification-dropdown"
        className="nav-link position-relative p-0 border-0 bg-transparent"
        style={{ boxShadow: 'none' }}
      >
        <span style={{ fontSize: '1.2rem' }}>🔔</span>
        {unreadCount > 0 && (
          <Badge
            bg="danger"
            pill
            className="position-absolute top-0 start-100 translate-middle"
            style={{ fontSize: '0.65rem' }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </Badge>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu className="notification-dropdown-menu shadow-lg">
        <div className="notification-dropdown-header px-3 py-2 border-bottom">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">Notifications</h6>
            <button
              className="btn btn-link btn-sm p-0 text-decoration-none"
              onClick={(e) => {
                e.stopPropagation();
                setShow(false);
                navigate('/notifications');
              }}
            >
              View All
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : notifications.length > 0 ? (
          <ListGroup variant="flush" className="notification-list">
            {notifications.map((notification) => (
              <ListGroup.Item
                key={notification.id}
                className={`notification-item ${notification.is_read === 0 ? 'unread' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="d-flex align-items-start">
                  <div className="flex-shrink-0 me-2">
                    <Badge bg={getNotificationColor(notification.notification_type)}>
                      {getNotificationIcon(notification.notification_type)}
                    </Badge>
                  </div>
                  <div className="flex-grow-1 notification-content">
                    <div className="notification-message">
                      {notification.message}
                      {notification.is_read === 0 && (
                        <Badge bg="danger" className="ms-1" style={{ fontSize: '0.6rem' }}>New</Badge>
                      )}
                    </div>
                    <div className="notification-time text-muted">
                      {formatTimeAgo(notification.created_at)}
                    </div>
                  </div>
                  <div className="flex-shrink-0 notification-actions">
                    {notification.is_read === 0 && (
                      <button
                        className="btn btn-sm btn-link p-0 me-2"
                        onClick={(e) => handleMarkAsRead(notification.id, e)}
                        title="Mark as read"
                      >
                        ✓
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-link p-0 text-danger"
                      onClick={(e) => handleDeleteNotification(notification.id, e)}
                      title="Delete"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        ) : (
          <div className="text-center py-4 text-muted">
            <div style={{ fontSize: '2rem' }}>🔔</div>
            <small>No notifications</small>
          </div>
        )}
      </Dropdown.Menu>
    </Dropdown>
  );
}

export default NotificationDropdown;
